import { captureMessage } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import { lazy, StrictMode, Suspense } from 'react';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { MemoryRouter } from 'react-router';

import ErrorBoundary from '@/components/app-shell/ErrorBoundary';
import posthog from '@/config/analytics/posthog';
import { getMsalInstance } from '@/config/auth/msal';
import {
  handleMsalConnectDialog,
  hasAuthResponseHash,
  isMsalConnectSurface,
} from '@/config/auth/msalDialogHandler';
import { OfficeProvider } from '@/config/office';
import { queryClient } from '@/config/query/queryClient';
import { initTheme } from '@/config/theme';
import { RecorderPage } from '@/pages/Recorder';
import { TimeoutError, withTimeout } from '@/utils/promise';

import App from './App';
import './config/i18n/i18n';
import './config/monitoring/sentry';

import './index.css';

// The bundle evaluated. Read by the watchdog in taskpane.html, which sits
// outside this module graph and is therefore the only thing that can still
// speak if evaluation dies partway through — as it did on Safari 17.1, where
// a Mac user stared at a white pane for seven weeks and produced no telemetry
// at all, because every reporter we have lives inside this file's imports.
window.__lucioBootPhase = 'bundle-evaluated';

// Resolve light/dark before first paint — all three root trees below rely on
// the `dark` class being on <html> already.
initTheme();

// Tell office.js we are here, before anything slow can happen.
//
// office.js starts a timer as it loads and throws "Office.js has not fully
// loaded. Your app must call Office.onReady()..." if nothing registers a
// readiness handler in time. initTheme() above already registers one; this
// call makes the handshake an explicit part of boot rather than a side effect
// of theming, so it cannot silently disappear if theme init changes.
// OfficeProvider still runs its own Office.onReady and remains the one surface
// that reports a real Office failure to the user.
function registerOfficeReadiness(): void {
  if (typeof Office === 'undefined' || typeof Office.onReady !== 'function') {
    return;
  }
  // Deliberately not awaited: outside an Office host `onReady` never settles,
  // and waiting on it here would reintroduce the very stall this change
  // removes. The rejection handler exists so a failed handshake surfaces as a
  // log line rather than an unhandled rejection.
  Office.onReady().catch((error: unknown) => {
    console.error('[Boot] Office.onReady rejected:', error);
  });
}

registerOfficeReadiness();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Get initial route from browser URL for MemoryRouter
const searchParams = new URLSearchParams(window.location.search);

/**
 * The URL fragment exactly as this window was opened with.
 *
 * Read synchronously, before anything renders: office.js rewrites the history
 * APIs and MSAL scrubs the hash while it works, so a later reading is not
 * trustworthy. `handleReturnLeg` captures it up front for the same reason.
 */
const bootHash = window.location.hash;
const pageParam = searchParams.get('page');
const initialPath = pageParam ? `/${pageParam}` : window.location.pathname || '/';

// Recorder App component that skips the heavy OfficeProvider logic
const RecorderApp = () => {
  useEffect(() => {
    // Just initialize Office to make sure Office.context is ready
    Office.onReady();
  }, []);
  return <RecorderPage />;
};

/**
 * Loaded on demand, and that is the point.
 *
 * `PdfViewerPage` pulls in pdfjs-dist, ~600 kB of the task pane bundle.
 * Imported statically it landed in the entry chunk, so every task pane boot
 * evaluated the whole PDF renderer before it could reach our own code — for a
 * viewer that only ever runs on the `/pdf-viewer` dialog route.
 *
 * That was not merely wasteful. pdfjs-dist v6 runs this at module scope
 * (build/pdf.mjs: `if (typeof Iterator.prototype.join !== "function")`), and
 * the `Iterator` global only exists from Safari 18.4. On an older WebKit that
 * is a ReferenceError before a line of our own code has run. And because this
 * module contains a top-level await, WebKit 17 reports nothing at all — no
 * console error, no `error` event, no rejection — which is how a Mac on
 * Safari 17.1 sat on a white pane for seven weeks with a clean console.
 * Reproduced in Playwright's WebKit 17.0 build: the old bundle halts silently
 * there; this one boots to the login page.
 *
 * Behind a dynamic import pdf.js can no longer take the rest of the app down
 * with it. The same ReferenceError now rejects one `import()` instead, and the
 * error boundary shows it: a host that cannot run pdf.js fails to open one
 * dialog instead of failing to boot at all.
 */
const PdfViewerPage = lazy(async () => {
  const mod = await import('@/pages/PdfViewer');
  return { default: mod.PdfViewerPage };
});

/**
 * Settles the boot watchdog once the lazy chunk has actually mounted.
 *
 * Effects in a suspended subtree do not run until it commits, so this fires
 * only after pdf.js has arrived. Marking the route settled any earlier would
 * switch the watchdog off while a chunk that never loads left the dialog on a
 * spinner forever.
 */
const PdfViewerReady = () => {
  useEffect(() => {
    window.__lucioBootPhase = 'rendered';
  }, []);
  return <PdfViewerPage />;
};

// PDF viewer dialog also opens in its own window with taskpane.html, so it
// loads office.js but doesn't need OfficeProvider's heavy auth/MSAL setup.
// Office.js still wants Office.onReady() to be called or it logs a fatal error.
const PdfViewerApp = () => {
  useEffect(() => {
    Office.onReady();
  }, []);
  return (
    // Not `null`: React has already cleared taskpane.html's boot shell by the
    // time this renders, so an empty fallback would blank the dialog for as
    // long as the pdf.js chunk takes to arrive — which is the symptom this
    // whole change exists to stop producing. Reuses the shell's own classes
    // and ids, defined in taskpane.html and therefore present in this window
    // too: the watchdog looks the ids up when it fires, so a chunk that never
    // arrives turns this spinner into the failure text instead of spinning
    // forever. The ids are unique because the shell they copy is already gone.
    <Suspense
      fallback={
        <div className="boot-shell">
          <div
            aria-label="Loading document viewer"
            className="boot-spinner"
            id="boot-spinner"
            role="status"
          />
          <div className="boot-failure" hidden id="boot-failure" role="alert">
            <p className="boot-failure-title">The document viewer couldn&apos;t finish loading.</p>
            <p className="boot-failure-body">
              Close this window and open the document again. If it keeps happening, please contact
              support.
            </p>
          </div>
        </div>
      }
    >
      <PdfViewerReady />
    </Suspense>
  );
};

/** The tree this window boots into. Every route shares the same providers. */
const bootTree = () => {
  if (initialPath === '/recorder') {
    return <RecorderApp />;
  }

  if (initialPath === '/pdf-viewer') {
    return <PdfViewerApp />;
  }

  return (
    <MemoryRouter initialEntries={[initialPath]} initialIndex={0}>
      <QueryClientProvider client={queryClient}>
        <OfficeProvider>
          <App />
        </OfficeProvider>
      </QueryClientProvider>
    </MemoryRouter>
  );
};

const renderApp = () => {
  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <ErrorBoundary>{bootTree()}</ErrorBoundary>
      </PostHogProvider>
    </StrictMode>,
  );

  // Settled only after render() returned: if createRoot or render throws, the
  // watchdog in taskpane.html must still fire on a pane that is in fact blank.
  // The PDF route is excluded on purpose — PdfViewerReady marks that one once
  // its lazy chunk has mounted, which is the only point at which it is true.
  if (initialPath !== '/pdf-viewer') {
    window.__lucioBootPhase = 'rendered';
  }
};

/** Redeems any pending MSAL redirect. Resolves once MSAL is ready to be used. */
const settleMsalRedirect = async (hash: string) => {
  const pca = await getMsalInstance();
  const response = await pca.handleRedirectPromise(hash);

  // If we are in a popup and successfully processed a token,
  // MSAL should strictly close the window.
  if (response && window.opener) {
    console.log('Processed redirect in popup, waiting for close...');
  }
};

/**
 * How long the OAuth-response boot waits before rendering anyway.
 *
 * Generous because this path does real work — redeeming a code with the
 * identity provider over the network. It is a backstop against a hang, not a
 * latency budget: a healthy redemption finishes far inside it, so the ordering
 * below is preserved in every normal case. The boot watchdog in taskpane.html
 * allows for it (REDIRECT_GRACE_MS) — keep the two in step.
 */
const MSAL_REDIRECT_TIMEOUT_MS = 15_000;

/**
 * Boot path for a window whose fragment carries an OAuth response.
 *
 * Keeps the original ordering — MSAL redeems the fragment before anything else
 * runs — because the response must be consumed before the app boot can touch
 * the history APIs. Renders in `finally`, so neither a throw nor a stall can
 * leave this window blank: the fragment was captured up front and is passed to
 * MSAL explicitly, so rendering late cannot take it away from a redemption
 * still in flight.
 */
const initializeApp = async (hash: string) => {
  try {
    await withTimeout(settleMsalRedirect(hash), MSAL_REDIRECT_TIMEOUT_MS, 'MSAL redirect');
  } catch (error) {
    console.error('Failed to handle redirect promise:', error);
    if (error instanceof TimeoutError) {
      captureMessage('Add-in boot: MSAL redirect did not settle', {
        level: 'warning',
        tags: { surface: 'app-boot-oauth', timeoutMs: String(MSAL_REDIRECT_TIMEOUT_MS) },
      });
    }
  } finally {
    renderApp();
  }
};

// Clerk login dialog/popup surface: this window exists only to run the
// centralized-auth handshake and relay the bootstrap JWT back to the taskpane.
// It must never boot the full app (React, MSAL, Office providers).
const hashParams = new URLSearchParams(bootHash.slice(1));
if (
  searchParams.get('clerkAuth') === '1' ||
  hashParams.has('bootstrap') ||
  hashParams.has('clerkError')
) {
  const { handleClerkAuthDialog } = await import('@/config/auth/clerkDialogHandler');
  // Set only once the handler's chunk has loaded: a failed import is still a
  // failed boot, and the watchdog should say so.
  window.__lucioBootPhase = 'auth-surface';
  await handleClerkAuthDialog();
} else if (isMsalConnectSurface(searchParams)) {
  // Connect Mailbox dialog surface: runs the MSAL redirect flow and relays the
  // identity token back to the taskpane. Must run before initializeApp — its
  // handleRedirectPromise call would otherwise consume the auth response.
  window.__lucioBootPhase = 'auth-surface';
  await handleMsalConnectDialog();
} else if (hasAuthResponseHash(bootHash)) {
  // This window carries an OAuth response that no auth surface claimed, so
  // initializeApp's handleRedirectPromise below will consume it and boot the
  // full app — inside the dialog window, if that is where we are. The taskpane
  // then waits out its whole timeout with no other trace. This is the only
  // signal that a surface-detection miss happened, so make it loud; routing is
  // deliberately left alone (the dev popup fallback relies on this branch, and
  // MSAL reads the popup's hash from the opener, not from this code).
  // oxlint-disable-next-line no-console
  console.error(
    '[Boot] an OAuth response reached the default app boot: no auth surface claimed this window, so the response is about to be consumed here. If this is the Connect Mailbox dialog, its sessionStorage marker was lost and the taskpane will time out.',
  );
  captureMessage('Unclaimed OAuth response on add-in boot', {
    extra: { search: window.location.search },
    level: 'error',
    // Tags so these are searchable/aggregatable; `extra` is not indexed.
    tags: {
      hasOpener: String(Boolean(window.opener)),
      isTopLevelWindow: String(window.top === window.self),
      responseKind: /[#&]error=/.test(bootHash) ? 'error' : 'code',
      surface: 'app-boot',
    },
  });
  window.__lucioBootPhase = 'redeeming-redirect';
  await initializeApp(bootHash);
} else {
  // Ordinary taskpane load: render, and leave MSAL alone.
  //
  // This boot used to await MSAL before the first render, so an MSAL call that
  // never settled left a permanently blank pane — no error, and no telemetry
  // either, because our pageview and user identification live inside React.
  // One user sat on a white pane for seven weeks.
  //
  // MSAL is not this app's identity — Clerk is — and the taskpane never starts
  // a redirect, so there is nothing here for handleRedirectPromise to redeem.
  // Running it anyway is not harmless: with no response to find it clears
  // MSAL's "interaction in progress" marker, which is cookie-backed and so
  // shared with a Connect Mailbox dialog. Late — after a slow initialize — it
  // could wipe the state of a dialog the user opened in the meantime, and the
  // dialog's return leg would come back with nothing to redeem. MSAL now starts
  // only when a taskpane caller asks for it (see getTaskpaneMsalInstance).
  renderApp();
}
